/**
 * Per-account Djay variant selection hook.
 *
 * Scopes the selected mascotte variant to the active learner account.
 * When switching accounts, each account gets their own variant (or defaults to 'default').
 *
 * Uses a module-level reactive store so ALL hook instances share the same
 * variant in real-time (e.g. FloatingDjay ↔ ProgressPage mascot collection).
 *
 * DDD co-locatie: moved from components/mascot/ in s60.
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { DjayVariant } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { dataLayer } from '../data/dataLayer'

const DEFAULT_VARIANT: DjayVariant = 'default'

// ── Module-level reactive store ──────────────────────────────────────────────
// Shared across all hook instances so variant changes propagate immediately.
let currentVariant: DjayVariant = DEFAULT_VARIANT
const subscribers = new Set<(v: DjayVariant) => void>()

function notifySubscribers() {
  subscribers.forEach((cb) => cb(currentVariant))
}

function setStoreVariant(variant: DjayVariant, accountId: string | null) {
  currentVariant = variant
  notifySubscribers()
  dataLayer.saveDjayVariant(
    accountId,
    variant === DEFAULT_VARIANT ? null : variant,
  )
}

function loadStoreVariant(accountId: string | null) {
  dataLayer.getDjayVariant(accountId).then((stored) => {
    currentVariant = (stored as DjayVariant) || DEFAULT_VARIANT
    notifySubscribers()
  })
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useDjaySelection() {
  const { activeAccount } = useAppStore()
  const accountId = activeAccount?.id ?? null
  const prevAccountIdRef = useRef<string | null>(accountId)

  const [selectedVariant, setSelectedVariantState] =
    useState<DjayVariant>(currentVariant)

  // Subscribe to module-level store changes
  useEffect(() => {
    const handler = (v: DjayVariant) => setSelectedVariantState(v)
    subscribers.add(handler)
    // Sync with current store value on mount
    setSelectedVariantState(currentVariant)
    return () => {
      subscribers.delete(handler)
    }
  }, [])

  // Load variant from dataLayer on mount
  useEffect(() => {
    loadStoreVariant(accountId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload on account switch
  useEffect(() => {
    if (prevAccountIdRef.current !== accountId) {
      prevAccountIdRef.current = accountId
      loadStoreVariant(accountId)
    }
  }, [accountId])

  const setSelectedVariant = useCallback(
    (variant: DjayVariant) => {
      setStoreVariant(variant, accountId)
    },
    [accountId],
  )

  return { selectedVariant, setSelectedVariant }
}
